/**
 * SwarmRing - EAHP Dense Orbital Ring
 * NOT a solid object - a cloud of thousands of individual satellites
 * Features: Sun-facing bright shields, dark outer faces with glowing emitters
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useStore } from '../../store/useStore';
import { EAHP_DATA } from '../../data/scientificData';

// === PRE-ALLOCATED OBJECTS FOR PERFORMANCE ===
const _sunPos = new THREE.Vector3();

interface SwarmRingProps {
  sunPosition?: [number, number, number];
  orbitRadius?: number;
  satelliteCount?: number;
  visible?: boolean;
}

// Dummy object for matrix calculations
const dummy = new THREE.Object3D();
const tempColor = new THREE.Color();

export function SwarmRing({
  sunPosition = [-30, 0, 0],
  orbitRadius = 8,
  satelliteCount = 5000,
  visible = true
}: SwarmRingProps) {
  // Sun-facing components (BRIGHT - reflective shields)
  const innerShieldRef = useRef<THREE.InstancedMesh>(null);
  const solarPanelRef = useRef<THREE.InstancedMesh>(null);

  // Outer-facing components (DARK - mechanical with glowing emitters)
  const outerHullRef = useRef<THREE.InstancedMesh>(null);
  const emitterGlowRef = useRef<THREE.InstancedMesh>(null);
  const emitterCoreRef = useRef<THREE.InstancedMesh>(null);

  const groupRef = useRef<THREE.Group>(null);
  const { view, isPaused, animationSpeed, isClipMode } = useStore();

  // Store satellite orbital data
  const satellites = useMemo(() => {
    const data: Array<{
      theta: number;
      verticalOffset: number;
      radialOffset: number;
      orbitSpeed: number;
      phase: number;
      wobbleSpeed: number;
      wobbleAmount: number;
      tier: 'micro' | 'standard' | 'heavy' | 'command';
      scale: number;
    }> = [];

    for (let i = 0; i < satelliteCount; i++) {
      // Dense ring distribution with natural variation
      const theta = Math.random() * Math.PI * 2;

      // Vertical offset - creates cloud-like thickness (not a perfect line)
      const verticalOffset = (Math.random() - 0.5) * 1.2;

      // Radial offset - creates ring thickness
      const radialOffset = (Math.random() - 0.5) * 0.8;

      // Satellite classification
      let tier: 'micro' | 'standard' | 'heavy' | 'command';
      let scale: number;

      const typeRoll = Math.random();
      if (typeRoll < 0.01) {
        tier = 'command'; // Rare large command stations
        scale = 0.15;
      } else if (typeRoll < 0.05) {
        tier = 'heavy'; // Heavy power collectors
        scale = 0.10;
      } else if (typeRoll < 0.25) {
        tier = 'standard'; // Standard satellites
        scale = 0.06;
      } else {
        tier = 'micro'; // Micro satellites (most common)
        scale = 0.035;
      }

      data.push({
        theta,
        verticalOffset,
        radialOffset,
        orbitSpeed: 0.008 + Math.random() * 0.012, // Varying orbital speeds
        phase: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.5 + Math.random() * 0.5,
        wobbleAmount: 0.02 + Math.random() * 0.03,
        tier,
        scale
      });
    }

    return data;
  }, [satelliteCount]);

  // Initialize instance matrices and colors
  useEffect(() => {
    if (!innerShieldRef.current || !outerHullRef.current ||
        !emitterGlowRef.current || !solarPanelRef.current ||
        !emitterCoreRef.current) return;

    const sunPos = new THREE.Vector3(...sunPosition);

    satellites.forEach((sat, i) => {
      const effectiveRadius = orbitRadius + sat.radialOffset;
      const x = sunPos.x + Math.cos(sat.theta) * effectiveRadius;
      const y = sunPos.y + sat.verticalOffset;
      const z = sunPos.z + Math.sin(sat.theta) * effectiveRadius;

      dummy.position.set(x, y, z);
      dummy.lookAt(sunPos); // Face the sun
      dummy.scale.setScalar(sat.scale);
      dummy.updateMatrix();

      // Apply same transform to all components
      innerShieldRef.current!.setMatrixAt(i, dummy.matrix);
      solarPanelRef.current!.setMatrixAt(i, dummy.matrix);
      outerHullRef.current!.setMatrixAt(i, dummy.matrix);
      emitterGlowRef.current!.setMatrixAt(i, dummy.matrix);
      emitterCoreRef.current!.setMatrixAt(i, dummy.matrix);

      // Color based on tier
      // Inner shields: Bright white/gold (sun-facing)
      if (sat.tier === 'command') {
        innerShieldRef.current!.setColorAt(i, tempColor.setHex(0xffffee));
        emitterGlowRef.current!.setColorAt(i, tempColor.setHex(0xff3300));
        emitterCoreRef.current!.setColorAt(i, tempColor.setHex(0xffff00));
      } else if (sat.tier === 'heavy') {
        innerShieldRef.current!.setColorAt(i, tempColor.setHex(0xffeecc));
        emitterGlowRef.current!.setColorAt(i, tempColor.setHex(0xff5500));
        emitterCoreRef.current!.setColorAt(i, tempColor.setHex(0xff8800));
      } else if (sat.tier === 'standard') {
        innerShieldRef.current!.setColorAt(i, tempColor.setHex(0xeeddbb));
        emitterGlowRef.current!.setColorAt(i, tempColor.setHex(0xff6600));
        emitterCoreRef.current!.setColorAt(i, tempColor.setHex(0xff6600));
      } else {
        innerShieldRef.current!.setColorAt(i, tempColor.setHex(0xddccaa));
        emitterGlowRef.current!.setColorAt(i, tempColor.setHex(0xff7700));
        emitterCoreRef.current!.setColorAt(i, tempColor.setHex(0xff5500));
      }
    });

    // Update all instance matrices
    innerShieldRef.current.instanceMatrix.needsUpdate = true;
    solarPanelRef.current.instanceMatrix.needsUpdate = true;
    outerHullRef.current.instanceMatrix.needsUpdate = true;
    emitterGlowRef.current.instanceMatrix.needsUpdate = true;
    emitterCoreRef.current.instanceMatrix.needsUpdate = true;

    // Update colors
    if (innerShieldRef.current.instanceColor) innerShieldRef.current.instanceColor.needsUpdate = true;
    if (emitterGlowRef.current.instanceColor) emitterGlowRef.current.instanceColor.needsUpdate = true;
    if (emitterCoreRef.current.instanceColor) emitterCoreRef.current.instanceColor.needsUpdate = true;
  }, [satellites, sunPosition, orbitRadius]);

  // Animation loop - orbital motion + wobble
  useFrame((state, delta) => {
    if (isPaused) return;

    const time = state.clock.elapsedTime * animationSpeed;
    // PERFORMANCE: Use pre-allocated vector
    _sunPos.set(sunPosition[0], sunPosition[1], sunPosition[2]);

    if (innerShieldRef.current && outerHullRef.current &&
        emitterGlowRef.current && solarPanelRef.current &&
        emitterCoreRef.current) {

      satellites.forEach((sat, i) => {
        // Orbital motion
        const currentTheta = sat.theta + time * sat.orbitSpeed;

        // Natural wobble - each satellite has slight independent motion
        const wobble = Math.sin(time * sat.wobbleSpeed + sat.phase) * sat.wobbleAmount;

        const effectiveRadius = orbitRadius + sat.radialOffset;
        const x = _sunPos.x + Math.cos(currentTheta) * effectiveRadius;
        const y = _sunPos.y + sat.verticalOffset + wobble;
        const z = _sunPos.z + Math.sin(currentTheta) * effectiveRadius;

        dummy.position.set(x, y, z);
        dummy.lookAt(_sunPos); // Always face the sun
        dummy.scale.setScalar(sat.scale);
        dummy.updateMatrix();

        innerShieldRef.current!.setMatrixAt(i, dummy.matrix);
        solarPanelRef.current!.setMatrixAt(i, dummy.matrix);
        outerHullRef.current!.setMatrixAt(i, dummy.matrix);
        emitterGlowRef.current!.setMatrixAt(i, dummy.matrix);
        emitterCoreRef.current!.setMatrixAt(i, dummy.matrix);
      });

      innerShieldRef.current.instanceMatrix.needsUpdate = true;
      solarPanelRef.current.instanceMatrix.needsUpdate = true;
      outerHullRef.current.instanceMatrix.needsUpdate = true;
      emitterGlowRef.current.instanceMatrix.needsUpdate = true;
      emitterCoreRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  if (!visible || !view.showEAHPSwarm) return null;

  return (
    <group ref={groupRef}>
      {/* ===== SUN-FACING COMPONENTS (BRIGHT) ===== */}

      {/* Inner Heat Shields - BRIGHT reflective white/gold cones facing sun */}
      <instancedMesh
        ref={innerShieldRef}
        args={[undefined, undefined, satelliteCount]}
        frustumCulled={false}
      >
        <coneGeometry args={[1, 1.8, 6]} />
        <meshStandardMaterial
          color={0xffeedd}
          metalness={0.98}
          roughness={0.02}
          emissive={0xffcc88}
          emissiveIntensity={0.3}
          envMapIntensity={3}
        />
      </instancedMesh>

      {/* Solar Collection Panels - Gold/copper tinted */}
      <instancedMesh
        ref={solarPanelRef}
        args={[undefined, undefined, satelliteCount]}
        frustumCulled={false}
      >
        <boxGeometry args={[3, 0.02, 0.6]} />
        <meshStandardMaterial
          color={0xcc9944}
          metalness={0.9}
          roughness={0.15}
          emissive={0x664422}
          emissiveIntensity={0.2}
        />
      </instancedMesh>

      {/* ===== OUTER-FACING COMPONENTS (DARK) ===== */}

      {/* Outer Hull - Dark mechanical silhouette */}
      <instancedMesh
        ref={outerHullRef}
        args={[undefined, undefined, satelliteCount]}
        frustumCulled={false}
      >
        <cylinderGeometry args={[0.5, 0.4, 1.2, 6]} />
        <meshStandardMaterial
          color={0x222222}
          metalness={0.6}
          roughness={0.7}
        />
      </instancedMesh>

      {/* Laser Emitter Glow - Bright points on dark side */}
      <instancedMesh
        ref={emitterGlowRef}
        args={[undefined, undefined, satelliteCount]}
        frustumCulled={false}
      >
        <sphereGeometry args={[0.35, 8, 8]} />
        <meshBasicMaterial
          color={0xff6600}
          transparent
          opacity={0.7}
          blending={THREE.AdditiveBlending}
          toneMapped={false}
        />
      </instancedMesh>

      {/* Emitter Core - Intense bright center */}
      <instancedMesh
        ref={emitterCoreRef}
        args={[undefined, undefined, satelliteCount]}
        frustumCulled={false}
      >
        <sphereGeometry args={[0.15, 6, 6]} />
        <meshBasicMaterial
          color={0xffff00}
          transparent
          opacity={1}
          blending={THREE.AdditiveBlending}
          toneMapped={false}
        />
      </instancedMesh>

      {/* ===== RING STRUCTURE ===== */}
      {/* NOTE: Removed solid metal rings - they looked like black circles against the Sun
          The swarm itself creates enough visual structure */}

      {/* NOTE: Command stations removed - only EAHP Construction Base remains as the main structure */}

      {/* Ambient ring glow - cohesive luminance */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={sunPosition}>
        <ringGeometry args={[orbitRadius - 0.8, orbitRadius + 0.8, 64]} />
        <meshBasicMaterial
          color={0xff5500}
          transparent
          opacity={0.06}
          blending={THREE.AdditiveBlending}
          side={THREE.DoubleSide}
          depthWrite={false}
          toneMapped={false}
        />
      </mesh>

      {/* NOTE: Construction base is now a separate EAHPConstructionBase component */}

      {/* Orbit visualization */}
      {view.showOrbits && (
        <mesh rotation={[Math.PI / 2, 0, 0]} position={sunPosition}>
          <ringGeometry args={[orbitRadius - 0.03, orbitRadius + 0.03, 128]} />
          <meshBasicMaterial
            color={0xff6600}
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}

      {/* Label - hidden during clip mode */}
      {view.showLabels && !isClipMode && (
        <group position={[sunPosition[0] + orbitRadius + 2.5, 2, 0]}>
          <sprite scale={[5, 1.25, 1]}>
            <spriteMaterial
              map={createLabelTexture()}
              transparent
              opacity={0.95}
            />
          </sprite>
        </group>
      )}
    </group>
  );
}

// Enhanced label texture
function createLabelTexture(): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;

  // Background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillRect(0, 0, 512, 128);

  // Border
  ctx.strokeStyle = '#ff6600';
  ctx.lineWidth = 3;
  ctx.strokeRect(3, 3, 506, 122);

  // Title
  ctx.fillStyle = '#ffaa00';
  ctx.font = 'bold 30px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('EAHP SWARM RING', 256, 42);

  // Subtitle
  ctx.fillStyle = '#ff8800';
  ctx.font = '16px monospace';
  ctx.fillText('0.1 AU | 5,000 ACTIVE COLLECTORS', 256, 82);

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

// Export EAHP data for info panel
export function useEAHPData() {
  return EAHP_DATA;
}
